import { useState } from 'react'
import { useRouter } from 'next/router'
import { useTranslations } from 'next-intl'
import {
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Button,
  useColorModeValue,
  VStack,
  InputGroup,
  InputRightElement,
  Box,
  Alert,
  AlertIcon,
  AlertDescription,
  useToast,
} from '@chakra-ui/react'
import { useForm } from 'react-hook-form'
import { useMutation } from '@apollo/client'

import { registerMutation } from '@/api/auth'
import { useLogin } from '@/hooks/useAuthActions'

type FormValues = {
  email: string
  password: string
}

export function RegisterForm() {
  const t = useTranslations()
  const router = useRouter()
  const [show, setShow] = useState(false)
  const handlePasswordVisibility = () => setShow(!show)
  const toast = useToast()

  const {
    handleSubmit,
    register,
    formState: { errors },
    getValues,
    reset,
  } = useForm<FormValues>()

  const [loginFunction] = useLogin(router)
  const [registerUser, { loading, error }] = useMutation(registerMutation, {
    onCompleted(data) {
      const { email, password } = getValues()
      if (data?.register?.boolean) {
        loginFunction(email, password)
        reset()
        toast({
          title: t('auth.register.success'),
          description: t('auth.register.success-description'),
          status: 'success',
          containerStyle: {
            marginTop: '50px',
          },
        })
      }
    },
  })

  function onSubmit(values: FormValues) {
    const { email, password } = values
    registerUser({
      variables: {
        email,
        password,
      },
    })
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <VStack spacing="4">
        <FormControl id="email" isInvalid={!!errors?.email}>
          <FormLabel>{t('auth.form.email')}</FormLabel>
          <Input
            type="email"
            {...register('email', {
              required: t('forms.errors.is-required'),
            })}
          />
          <FormErrorMessage>
            {errors.email && errors.email.message}
          </FormErrorMessage>
        </FormControl>
        <FormControl id="password" isInvalid={!!errors?.password}>
          <FormLabel>{t('auth.form.password')}</FormLabel>
          <InputGroup size="md">
            <Input
              type={show ? 'text' : 'password'}
              {...register('password', {
                required: t('forms.errors.is-required'),
                minLength: {
                  value: 8,
                  message: t('forms.errors.min-length', { length: 8 }),
                },
              })}
            />
            <InputRightElement width="4.5rem">
              <Button
                h="1.75rem"
                size="sm"
                bg={useColorModeValue('gray.300', 'gray.700')}
                _hover={{
                  bg: useColorModeValue('gray.400', 'gray.800'),
                }}
                onClick={handlePasswordVisibility}
              >
                {show ? t('buttons.hide') : t('buttons.show')}
              </Button>
            </InputRightElement>
          </InputGroup>
          <FormErrorMessage>
            {errors.password && errors.password.message}
          </FormErrorMessage>
        </FormControl>
        <Box w="100%">
          <Button
            type="submit"
            variant="brand"
            w="100%"
            mt="4"
            isLoading={loading}
          >
            {t('buttons.create-account')}
          </Button>
        </Box>
        {error && (
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            <AlertDescription>{error.message}</AlertDescription>
          </Alert>
        )}
      </VStack>
    </form>
  )
}
